import React, {forwardRef, useRef} from 'react'
import PropTypes from 'prop-types'
import {Box, Heading, IconButton, Skeleton, Stack} from '@chakra-ui/react'
import ProductTile from '../../components/product-tile'
import {ChevronLeftIcon, ChevronRightIcon} from '@app/components/icons'
import {useIntl} from 'react-intl'

/**
 * ProductScroller Component
 *
 * A component that displays a scrollable list of products. It supports both desktop and mobile views,
 * with different layouts for each. The component can display a loading skeleton, a header, and a title.
 * It also provides navigation buttons for scrolling through the products on larger screens.
 *
 * @component
 * @param {Object} props - The properties object.
 * @param {React.ReactNode} [props.header] - Optional header element to display above the product list.
 * @param {string} [props.title] - Optional title to display above the product list.
 * @param {Array<Object>} props.products - An array of product objects to display.
 * @param {boolean} [props.isLoading] - Flag indicating whether the product data is still loading.
 * @param {Object} [props.scrollProps] - Additional props to pass to the scrollable container.
 * @param {string|number|Object} [props.itemWidth] - Width of each product item, responsive to screen size.
 * @param {Function|Object} [props.productTileProps] - Additional props or a function returning props for each ProductTile.
 * @param {React.Ref} ref - A ref that can be attached to the component.
 *
 * @returns {JSX.Element|null} The rendered component or null if there are no products and not loading.
 *
 * @example
 * const products = [{ id: 1, name: 'Product 1' }, { id: 2, name: 'Product 2' }];
 * const handleProductClick = (productId) => console.log(productId);
 *
 * <ProductScroller
 *   products={products}
 *   onProductClick={handleProductClick}
 *   className="custom-class"
 * />
 */
const ProductScroller = forwardRef(
    (
        {
            header,
            title,
            products = [],
            isLoading,
            scrollProps,
            itemWidth = {base: '70%', md: '40%', lg: 'calc(33.33% - 10px)'},
            productTileProps,
            ...props
        },
        ref
    ) => {
        const intl = useIntl()
        const scrollRef = useRef()

        if ((!products || products.length < 1) && !isLoading) {
            return null
        }

        /**
         * Scrolls the product scroller horizontally by the width of the visible area.
         *
         * @param {number} [direction=1] - The direction to scroll. Use 1 for right and -1 for left.
         *                                 Defaults to 1 (right).
         */
        const scroll = (direction = 1) => {
            scrollRef.current?.scrollBy({
                top: 0,
                left: direction * scrollRef.current?.offsetWidth,
                behavior: 'smooth'
            })
        }

        return (
            <Box position="relative" data-testid="product-scroller" ref={ref}>
                <Stack spacing={6} {...props} width={{base: '100%'}}>
                    {isLoading && <Skeleton height={6} width="150px" m="auto" />}

                    {title && !header && !isLoading && (
                        <Heading as="h2" fontSize="4xl" pb={'20px'} pt={'20px'} pl={'10px'}>
                            {title}
                        </Heading>
                    )}

                    {!title && !isLoading && header}

                    <Stack
                        ref={scrollRef}
                        direction="row"
                        spacing={4}
                        wrap="nowrap"
                        overflowX="scroll"
                        px={{base: 4, md: 8, lg: 0}}
                        py={1}
                        {...scrollProps}
                        sx={{
                            scrollPadding: {base: 16, md: 32, lg: 0},
                            scrollSnapType: 'x mandatory',
                            WebkitOverflowScrolling: 'touch',
                            ...scrollProps?.sx
                        }}
                    >
                        {products.map((product, idx) => (
                            <Box
                                key={product?.id || idx}
                                flex="0 0 auto"
                                width={itemWidth}
                                style={{scrollSnapAlign: 'start'}}
                            >
                                <ProductTile
                                    data-testid="product-scroller-item"
                                    product={product}
                                    {...(typeof productTileProps === 'function'
                                        ? {...productTileProps(product)}
                                        : {...productTileProps})}
                                    dynamicImageProps={{
                                        widths: ['70vw', '70vw', '40vw', '30vw']
                                    }}
                                />
                            </Box>
                        ))}
                    </Stack>
                </Stack>

                {!isLoading && products?.length > 3 && (
                    <>
                        <Box
                            display={{
                                base: 'none',
                                lg: 'block'
                            }}
                            position="absolute"
                            top="50%"
                            left={{base: 0, lg: 4}}
                            transform="translateY(-50%)"
                        >
                            <IconButton
                                data-testid="product-scroller-nav-left"
                                aria-label={intl.formatMessage({
                                    id: 'product_scroller.assistive_msg.scroll_left',
                                    defaultMessage: 'Scroll products left'
                                })}
                                icon={<ChevronLeftIcon color="black" />}
                                borderRadius="full"
                                colorScheme="whiteAlpha"
                                onClick={() => scroll(-1)}
                            />
                        </Box>

                        <Box
                            display={{
                                base: 'none',
                                lg: 'block'
                            }}
                            position="absolute"
                            top="50%"
                            right={{base: 0, lg: 4}}
                            transform="translateY(-50%)"
                        >
                            <IconButton
                                data-testid="product-scroller-nav-right"
                                aria-label={intl.formatMessage({
                                    id: 'product_scroller.assistive_msg.scroll_right',
                                    defaultMessage: 'Scroll products right'
                                })}
                                icon={<ChevronRightIcon color="black" />}
                                borderRadius="full"
                                colorScheme="whiteAlpha"
                                onClick={() => scroll(1)}
                            />
                        </Box>
                    </>
                )}
            </Box>
        )
    }
)

ProductScroller.displayName = 'ProductScroller'

ProductScroller.propTypes = {
    header: PropTypes.any,
    title: PropTypes.any,
    products: PropTypes.array,
    isLoading: PropTypes.bool,
    scrollProps: PropTypes.object,
    itemWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.object]),
    productTileProps: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
    maxProducts: PropTypes.number
}

export default ProductScroller
